import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Gift, AlertCircle } from 'lucide-react';
import { useAuth } from '@/App';

/**
 * MembershipCountdownChip
 *
 * Small pill shown to logged-in members that counts down the days left on
 * their complimentary membership. Colors escalate as expiry nears:
 *   > 60 days  → emerald (perk visible & unobtrusive)
 *   30-60 days → amber
 *   < 30 days  → red (urgent reminder)
 * Renders nothing if the user is not a member or has no expiry set.
 */
const MembershipCountdownChip = ({ className = '' }) => {
  const { user } = useAuth();

  const info = useMemo(() => {
    if (!user || !user.is_member || !user.membership_expires) return null;
    const expires = new Date(user.membership_expires);
    if (isNaN(expires.getTime())) return null;
    const now = new Date();
    const msPerDay = 1000 * 60 * 60 * 24;
    const daysLeft = Math.ceil((expires.getTime() - now.getTime()) / msPerDay);
    if (daysLeft <= 0) return { expired: true };

    let tone = 'emerald';
    if (daysLeft < 30) tone = 'red';
    else if (daysLeft <= 60) tone = 'amber';

    return {
      daysLeft,
      expiryDateStr: expires.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
      tone,
    };
  }, [user]);

  if (!info) return null;

  // Expired state — friendlier CTA
  if (info.expired) {
    return (
      <Link
        to="/membership"
        data-testid="membership-countdown-chip"
        className={`inline-flex items-center gap-2 rounded-full border-2 border-red-300 bg-red-50 text-red-700 px-4 py-2 text-sm font-semibold hover:bg-red-100 transition-colors ${className}`}
      >
        <AlertCircle className="w-4 h-4 shrink-0" />
        Your complimentary membership has ended — renew now
      </Link>
    );
  }

  const toneClasses = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100',
    amber: 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100',
    red: 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100',
  }[info.tone];

  const iconTone = {
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
    red: 'text-red-600',
  }[info.tone];

  return (
    <Link
      to="/membership"
      data-testid="membership-countdown-chip"
      title={`Your complimentary membership is active until ${info.expiryDateStr}`}
      className={`inline-flex items-center gap-2 rounded-full border-2 ${toneClasses} px-4 py-2 text-sm font-semibold transition-colors max-w-full ${className}`}
    >
      <Gift className={`w-4 h-4 shrink-0 ${iconTone}`} />
      <span className="truncate">
        <span className="font-bold">{info.daysLeft.toLocaleString()}</span> {info.daysLeft === 1 ? 'day' : 'days'} of free membership left
      </span>
    </Link>
  );
};

export default MembershipCountdownChip;
